// src/components/ui/button.jsx

import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-text-on-primary shadow hover:bg-primary-dark active:bg-primary-dark",
        destructive:
          "bg-danger text-white shadow-sm hover:bg-danger-dark active:bg-danger-dark",
        outline:
          "border border-neutral-dark/30 bg-transparent text-text shadow-sm hover:bg-neutral-light hover:border-primary",
        secondary:
          "bg-secondary text-text shadow-sm hover:bg-secondary-dark active:bg-secondary-dark",
        ghost:
          "hover:bg-neutral-light text-text",
        link:
          "text-primary underline-offset-4 hover:underline",
        success:
          "bg-success text-white shadow-sm hover:bg-success-dark active:bg-success-dark",
        warning:
          "bg-warning text-text shadow-sm hover:bg-warning-dark active:bg-warning-dark",
        glass:
          "bg-glass-light backdrop-blur-md border border-white/20 text-text hover:bg-white/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? React.Fragment : "button";
    
    if (asChild) {
      return React.cloneElement(children, {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props,
      });
    }
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
